import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { s3Static } from '../../utils/constants'
import { getBlogCategories, marketBlogsPath } from '../../utils/markets'

// UAE / Malaysia versions of the Singapore blog furniture, with the same
// markup and classes so the three countries' blogs look alike:
//  - MarketBlogCards: the card grid of components/blogs/BlogList.js (title,
//    photo, snippet, "Continue Reading").
//  - MarketBlogTopics: the dark link bar of components/blogs/BlogHeaderLinks.js.
//
// Separate components because the Singapore ones are wired to Singapore:
// BlogItem routes WordPress posts by SG category to SG URLs, BlogsList
// prefixes every image with the S3 path, and BlogHeaderLinks lists the SG
// categories. Cards come ready-made from lib/market-blogs.js.

/**
 * cards: [{ href, title, image, imageAlt, snippet, lang }] - image is a full
 * URL. Pass exactly the cards to show (the listing passes all, a guide six).
 */
export const MarketBlogCards = ({ cards = [] }) => (
    <div>
        <div className='grid grid-cols-2 md:grid-cols-3 max-w-6xl mx-auto gap-x-3 sm:gap-x-5 gap-y-3 py-5'>
            {cards.map((card, i) => (
                <div className='p-2.5 md:p-4 space-y-1.5 md:space-y-2 mt-2 md:mt-5 shadow rounded-xl border' key={card.href} lang={card.lang || undefined}>
                    <Link href={card.href} prefetch={false} className='text-orange-600'>
                        <h3 className='md:h-20 font-bold text-center text-sm md:text-base line-clamp-3 md:line-clamp-none'>{card.title}</h3>
                    </Link>
                    <div className='relative w-full aspect-video md:aspect-4/3 mt-2 md:mt-3 rounded-xl overflow-hidden'>
                        <Image
                            src={card.image ?? s3Static + 'logo.png'}
                            fill
                            style={{ objectFit: 'cover' }}
                            placeholder='blur'
                            blurDataURL={s3Static + 'placeholder.png'}
                            alt={card.imageAlt}
                            sizes='(max-width: 768px) 50vw, 33vw'
                            priority={i < 3}
                        />
                    </div>
                    <p className='hidden md:block text-sm text-gray-500 leading-7 overflow-hidden'>
                        {card.snippet}
                    </p>
                    <div className='pt-2 pb-1 md:py-10 text-center'>
                        <Link href={card.href} prefetch={false} className='inline-block text-orange-600 text-xs md:text-base px-2.5 py-1.5 md:px-4 md:py-3 border border-amber-600 rounded-xl'>
                            {card.lang === 'ms' ? 'Baca Seterusnya' : 'Continue Reading'}
                        </Link>
                    </div>
                </div>
            ))}
        </div>
    </div>
)

// The country's blog home, then its category pages (AC / aircond guides,
// fitness guides) - like the Singapore bar of blog categories.
export const MarketBlogTopics = ({ market }) => {
    const links = [
        { href: marketBlogsPath(market.countryCd), label: 'All Guides' },
        ...getBlogCategories(market.countryCd).map((c) => ({ href: c.path, label: c.label })),
    ]
    return (
        <div className='bg-gray-800 text-white py-10 px-2'>
            <ul className='max-w-5xl mx-auto flex flex-wrap justify-center gap-x-10 gap-y-6 text-center text-sm md:text-base'>
                {links.map((link) => (
                    <li key={link.href}>
                        <Link href={link.href} prefetch={false} className='text-orange-500'>
                            {link.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    )
}

// End of a guide or post, as on the Singapore blog pages: six cards, then the
// "Topics" heading and link bar.
export const MarketBlogFooter = ({ market, cards = [], currentPath, lang = 'en' }) => {
    const shown = cards.filter((card) => card.href !== currentPath).slice(0, 6)
    return (
        <>
            {shown.length > 0 && (
                <div className='max-w-6xl mx-auto px-2'>
                    <MarketBlogCards cards={shown} />
                </div>
            )}
            <h2 className='bg-gray-800 text-white pt-10 pb-4 p-2 text-center text-2xl'>
                {lang === 'ms' ? 'Topik' : 'Topics'}
            </h2>
            <MarketBlogTopics market={market} />
        </>
    )
}
